import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from 'contexts/AuthContext';
import { PortalProfileProvider } from 'contexts/PortalProfileContext';
import { ThemeProvider } from 'contexts/ThemeContext';
import SoftphoneWidget from 'telephony/SoftphoneWidget';
import Extensions from 'pages/Telephony/Extensions';
import Directory from 'pages/Telephony/Directory';
import OutboundCall from 'pages/Telephony/OutboundCall';
import AgentConsole from 'pages/Telephony/AgentConsole';
import AgentGroups from 'pages/Telephony/AgentGroups';
import { useAuth } from 'hooks/useAuth';
import Login from 'pages/Login';
import Authorize from 'pages/OAuth/Authorize';
import Privacy from 'pages/Legal/Privacy';
import Terms from 'pages/Legal/Terms';
import ConnectorDocs from 'pages/Legal/ConnectorDocs';
import SocialComplete from 'pages/Auth/SocialComplete';
import Signup from 'pages/Signup';
import ResetPassword from 'pages/ResetPassword';
import Dashboard from 'pages/Dashboard';
import CallLogs from 'pages/CallLogs';
import CallFailures from 'pages/CallFailures';
import SystemHealth from 'pages/SystemHealth';
import AudioSessions from 'pages/AudioSessions';
import Recordings from 'pages/Recordings';
import Transfers from 'pages/Transfers';
import PhoneNumbers from 'pages/PhoneNumbers';
import TransferProfiles from 'pages/TransferProfiles';
import Providers from 'pages/Providers';
import CapacityLimits from 'pages/CapacityLimits';
import TelephonyOverview from 'pages/TelephonyOverview';
import IvrStates from 'pages/IvrStates';
import WorkerConfigs from 'pages/WorkerConfigs';
import Businesses from 'pages/Businesses';
import Users from 'pages/Users';
import CustomReports from 'pages/CustomReports';
import Contacts from 'pages/Contacts';
import ContactProfile from 'pages/Contacts/ContactProfile';
import ContactDuplicates from 'pages/Contacts/ContactDuplicates';
import ProviderAccess from 'pages/Settings/ProviderAccess';
import ContactGate from 'pages/Settings/ContactGate';
import PortalProfiles from 'pages/Platform/PortalProfiles';
import WhatsAppDashboard from 'pages/WhatsApp/WhatsAppDashboard';
import WhatsAppLeadAssignment from 'pages/WhatsApp/WhatsAppLeadAssignment';
import WhatsAppReplyRules from 'pages/WhatsApp/WhatsAppReplyRules';
import WhatsAppAiAgent from 'pages/WhatsApp/WhatsAppAiAgent';
import TraiCompliance from 'pages/Compliance/TraiCompliance';
import OperatorConsole from 'pages/Operator/OperatorConsole';
import WhatsAppCampaignBuilder from 'pages/WhatsApp/WhatsAppCampaignBuilder';
import WhatsAppCampaignDetail from 'pages/WhatsApp/WhatsAppCampaignDetail';
import WhatsAppSetup from 'pages/WhatsApp/WhatsAppSetupV2';
import WhatsAppTemplates from 'pages/WhatsApp/WhatsAppTemplates';
import WhatsAppChats from 'pages/WhatsApp/WhatsAppChats';
import DataPrivacy from 'pages/Settings/DataPrivacy';
import SocialSignIn from 'pages/Settings/SocialSignIn';
import BookingIntegrations from 'pages/Settings/BookingIntegrations';
import WhatsAppCampaigns from 'pages/WhatsApp/WhatsAppCampaigns';
import WhatsAppMessageLogs from 'pages/WhatsApp/WhatsAppMessageLogs';
import EmbeddedSignupConfig from 'pages/WhatsApp/EmbeddedSignupConfig';
import WABARegistrations from 'pages/WhatsApp/WABARegistrations';
import EmailCampaigns from 'pages/Email/EmailCampaigns';
import EmailCampaignBuilder from 'pages/Email/EmailCampaignBuilder';
import EmailCampaignDetail from 'pages/Email/EmailCampaignDetail';
import EmailSuppressions from 'pages/Email/EmailSuppressions';
import EmailTemplates from 'pages/Email/EmailTemplates';
import EmailProviderSettings from 'pages/Email/EmailProviderSettings';
import TelephonyDashboard from 'pages/Telephony/TelephonyDashboard';
import AIAgentCreation from 'pages/Agents/AIAgentCreation';
import AIAgents from 'pages/Agents/AIAgents';
import AgentConfigEditor from 'pages/Agents/AgentConfigEditor';
import LeadOverview from 'pages/Crm/LeadOverview';
import MemoryProfiles from 'pages/Agents/MemoryProfiles';
import VoiceAiIntegrations from 'pages/Agents/VoiceAiIntegrations';
import CrmIntegrations from 'pages/Crm/CrmIntegrations';
import BookingSetup from 'pages/Crm/BookingSetup';
import CustomApiDeliveries from 'pages/Crm/CustomApiDeliveries';
import { CUSTOM_API_DELIVERY_ROLES } from 'pages/Crm/customApiShapes';
import CallActivity from 'pages/Crm/CallActivity';
import Captures from 'pages/Crm/Captures';
import IVRCreation from 'pages/Telephony/IVRCreation';
import IvrSwitchSync from 'pages/Telephony/IvrSwitchSync';
import IvrAppEditor from 'pages/Telephony/IvrAppEditor';
import IVRApps from 'pages/Telephony/IVRApps';
import WebhookStubs from 'pages/Developer/WebhookStubs';
import CallDataSources from 'pages/Telephony/CallDataSources';
import Softphone from 'pages/Telephony/Softphone';
import LiveCalls from 'pages/Telephony/LiveCalls';
import VIPManagement from 'pages/Telephony/VIPManagement';
import DNDManagement from 'pages/Telephony/DNDManagement';
import BusinessHours from 'pages/Telephony/BusinessHours';
// The Standard campaign page is reached only through <VoiceCampaigns />, and is
// flag-hidden (FEATURE_FLAGS.SHOW_STANDARD_CAMPAIGNS). Nothing here routes to it
// directly, so the import stays commented out until the flag comes back on.
// import CampaignManagement from 'pages/Telephony/CampaignManagement';
import VoiceCampaigns from 'pages/Telephony/VoiceCampaigns';
import UserDirectoryOverview from 'pages/UserDirectory/UserDirectoryOverview';
import AuditLogs from 'pages/AuditLogs';
import FeatureLocks from 'pages/Platform/FeatureLocks';
import FeatureAccess from 'pages/Platform/FeatureAccess';
import Seats from 'pages/Platform/Seats';
import NumberOwnership from 'pages/Platform/NumberOwnership';
import BillingDashboard from 'pages/Billing/BillingDashboard';
import CreditAccounts from 'pages/Billing/CreditAccounts';
import ServiceRates from 'pages/Billing/ServiceRates';
import Invoices from 'pages/Billing/Invoices';
import Payments from 'pages/Billing/Payments';
import UsageDashboard from 'pages/Billing/UsageDashboard';
import DeferredRevenue from 'pages/Billing/DeferredRevenue';
import TestNumbers from 'pages/Billing/TestNumbers';
import MyBilling from 'pages/Billing/MyBilling';
import { MY_BILLING_ROLES } from 'pages/Billing/myBillingRules';
import PaymentGateways from 'pages/Billing/PaymentGateways';
import { TEST_NUMBER_ROLES } from 'pages/Billing/testNumberRules';
import { DEFERRED_REVENUE_ROLES, PLATFORM_BILLING_ROLES } from 'pages/Billing/billingReportRules';
import ApiKeys from 'pages/Developer/ApiKeys';
import Webhooks from 'pages/Developer/Webhooks';
import ApiDocumentation from 'pages/Developer/ApiDocumentation';
import ApiTester from 'pages/Developer/ApiTester';
import Settings from 'pages/Settings/Settings';
import Notifications from 'pages/Notifications';
// THE PAGES BEHIND ComingSoon ARE STILL IN THE TREE, and are deliberately not
// imported here. /revenue, /analytics, /support, every /sms/* and /rcs/*, and
// (Billing's ServiceRates is routed again since 2026-10-13) all render <ComingSoon/>
// while their real components sit unchanged under pages/. Importing them without routing them made every
// CI build fail on thirteen no-unused-vars errors, which is how this repo
// arrived at a red build nobody could deploy through.
//
// To switch one back on: re-add its import and swap the <ComingSoon/> in its
// Route for the component. Nothing else was removed.
import ComingSoon from 'pages/ComingSoon';
import Layout from 'components/Layout';
import { canOpenPath, navPrincipal } from 'components/Layout/navVisibility';
import { useFeatureAccess } from 'hooks/useFeatureAccess';
import { useFeatureGrants } from 'hooks/useFeatureGrants';
import { useSoftphoneEntitlement } from 'hooks/useSoftphoneEntitlement';

// Workflows ship dark (FLAGGED_PATHS 'workflows'), so they are split out: a build without
// the flag never downloads the canvas. PrivateRoute's canOpenPath bounces the URL too.
const WorkflowsList = React.lazy(() => import('pages/Workflows/WorkflowsList'));
const WorkflowEditor = React.lazy(() => import('pages/Workflows/WorkflowEditor'));
const WorkflowRuns = React.lazy(() => import('pages/Workflows/WorkflowRuns'));
const WorkflowRunDetail = React.lazy(() => import('pages/Workflows/WorkflowRuns').then((m) => ({ default: m.WorkflowRunDetail })));
const lazyPage = (el: React.ReactNode) => (
  <React.Suspense fallback={<div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>Loading…</div>}>{el}</React.Suspense>
);

/**
 * Route guard.
 *
 * `roles` restricts a route to specific roles. Without it the route is open to
 * any authenticated user, which is the default for most of the app.
 *
 * This is a usability guard, not a security boundary — it stops someone landing
 * somewhere they shouldn't by typing a URL, but the API is what actually has to
 * refuse the request. Any route gated here should be gated server-side too.
 */
const PrivateRoute: React.FC<{ children: React.ReactNode; roles?: string[] }> = ({ children, roles }) => {
  const { isAuthenticated, loading, user, currentOrganization } = useAuth();
  const location = useLocation();
  // Above the early returns below: hooks may not be called conditionally.
  const features = useFeatureAccess();
  const grants = useFeatureGrants();
  const softphone = useSoftphoneEntitlement();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const principal = navPrincipal(currentOrganization, user, features, grants, softphone?.allowed);

  if (roles && !roles.some(r => r.toLowerCase() === principal.role)) {
    return <Navigate to="/" replace />;
  }

  // Platform-operator routes for a BUSINESS tenant. Hiding the sidebar link
  // leaves the URL typeable — and a stale bookmark survives an org switch — so
  // the same rule is applied here, on every route, without having to remember
  // to pass `roles` on each one (most of them don't). Same caveat as above: it
  // redirects a wrong turn, it does not protect the data.
  if (!canOpenPath(location.pathname, principal)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Keep in step with the DEVELOPERS group in Sidebar.tsx / MobileBottomNav.tsx.
// 'developer' is inert until the backend issues that role.
const DEVELOPER_ROLES = ['master_admin', 'super_admin', 'service_admin', 'operator_admin', 'developer'];
const API_TESTER_ROLES = ['master_admin', 'super_admin', 'service_admin', 'platform_admin'];
// Webhook stubs are a self-service feature every tenant is meant to have, so
// this is wider than DEVELOPER_ROLES — which excludes the two *_user roles and
// would have hidden it from the people most likely to be wiring up an
// integration. The backend gates the same endpoints at its MEMBERS list, which
// already includes the business and provider roles.
const STUB_ROLES = ['master_admin', 'super_admin', 'service_admin', 'service_user',
                    'operator_admin', 'operator_user', 'developer'];

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PortalProfileProvider>
        <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* PUBLIC on purpose. An assistant sends the person straight here, so
              the page handles its own sign-in redirect rather than being wrapped
              in PrivateRoute — which would drop them on the dashboard and
              abandon the authorisation half-done. */}
          <Route path="/authorize" element={<Authorize />} />
          {/* PUBLIC. The login screen links here, so a reader who has not signed in
              is exactly the person who needs it — and a connector directory listing
              requires a reachable privacy policy URL. */}
          {/* PUBLIC. Somebody arriving from an assistant or a directory listing has no
              account yet; without this they reach a sign-in page for one they do not have. */}
          <Route path="/signup" element={<Signup />} />
          {/* PUBLIC. Where a provider's callback sends the browser back, carrying a
              single-use code rather than a session. */}
          <Route path="/auth/social/complete" element={<SocialComplete />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* PUBLIC. The signup checkbox has linked to /terms since the brand layer
              was written and nothing answered it, so people were asked to agree to a
              blank tab. */}
          <Route path="/terms" element={<Terms />} />
          {/* PUBLIC. The docs URL a connector directory listing requires, and what a
              prospective customer reads before they have an account. */}
          <Route path="/docs/connector" element={<ConnectorDocs />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/calls"
            element={
              <PrivateRoute>
                <Layout>
                  <CallLogs />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/call-failures"
            element={
              <PrivateRoute>
                <Layout>
                  <CallFailures />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/system-health"
            element={
              /* Platform infrastructure: Green Giraffe master/platform admins only (14 Oct 2026). */
              <PrivateRoute roles={['master_admin', 'platform_admin']}>
                <Layout>
                  <SystemHealth />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/sessions"
            element={
              <PrivateRoute>
                <Layout>
                  <AudioSessions />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/recordings"
            element={
              <PrivateRoute>
                <Layout>
                  <Recordings />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/transfers"
            element={
              <PrivateRoute>
                <Layout>
                  <Transfers />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/phones"
            element={
              <PrivateRoute>
                <Layout>
                  <PhoneNumbers />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/console"
            element={
              <PrivateRoute>
                <Layout>
                  <AgentConsole />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/directory"
            element={
              <PrivateRoute>
                <Layout>
                  <Directory />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/outbound"
            element={
              <PrivateRoute>
                <Layout>
                  <OutboundCall />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/extensions"
            element={
              <PrivateRoute>
                <Layout>
                  <Extensions />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/groups"
            element={
              <PrivateRoute>
                <Layout>
                  <AgentGroups />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/transfer-profiles"
            element={
              <PrivateRoute>
                <Layout>
                  <TransferProfiles />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/providers"
            element={
              <PrivateRoute>
                <Layout>
                  <Providers />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/overview"
            element={
              <PrivateRoute>
                <Layout>
                  <TelephonyOverview />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/capacity-limits"
            element={
              <PrivateRoute>
                <Layout>
                  <CapacityLimits />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* NOTE: /telephony/campaigns is served by <VoiceCampaigns /> further
              down. An older duplicate route for this same path used to sit here
              and shadowed it — React Router takes the first match — so the
              three-variant unification was unreachable. Do not re-add a second
              route for this path. */}
          <Route
            path="/telephony/ivr/states"
            element={
              <PrivateRoute>
                <Layout>
                  <IvrStates />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/workers/config"
            element={
              <PrivateRoute>
                <Layout>
                  <WorkerConfigs />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/businesses"
            element={
              <PrivateRoute>
                <Layout>
                  <Businesses />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute>
                <Layout>
                  <Users />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute>
                <Layout>
                  <CustomReports />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Legacy path — canonical home is /user-directory/contacts */}
          <Route path="/contacts" element={<Navigate to="/user-directory/contacts" replace />} />
          <Route
            path="/whatsapp"
            element={
              <PrivateRoute>
                <Layout>
                  <WhatsAppDashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/compliance/categories"
            element={
              <PrivateRoute>
                <Layout>
                  <TraiCompliance tab="categories" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/compliance/numbers"
            element={
              <PrivateRoute>
                <Layout>
                  <TraiCompliance tab="numbers" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/compliance/consent"
            element={
              <PrivateRoute>
                <Layout>
                  <TraiCompliance tab="consent" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/compliance/complaints"
            element={
              <PrivateRoute>
                <Layout>
                  <TraiCompliance tab="complaints" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route path="/compliance" element={<Navigate to="/compliance/categories" replace />} />
          <Route
            path="/whatsapp/setup"
            element={
              <PrivateRoute>
                <Layout>
                  <WhatsAppSetup />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/whatsapp/templates"
            element={
              <PrivateRoute>
                <Layout>
                  <WhatsAppTemplates />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/whatsapp/chats"
            element={
              <PrivateRoute>
                <Layout>
                  <WhatsAppChats />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/whatsapp/campaigns"
            element={
              <PrivateRoute>
                <Layout>
                  <WhatsAppCampaigns />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/whatsapp/message-logs"
            element={
              <PrivateRoute>
                <Layout>
                  <WhatsAppMessageLogs />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/whatsapp/lead-assignment"
            element={
              <PrivateRoute>
                <Layout>
                  <WhatsAppLeadAssignment />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/whatsapp/reply-rules"
            element={
              <PrivateRoute>
                <Layout>
                  <WhatsAppReplyRules />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/whatsapp/ai-agent"
            element={
              <PrivateRoute>
                <Layout>
                  <WhatsAppAiAgent />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/ask"
            element={
              <PrivateRoute>
                <Layout>
                  <OperatorConsole />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/whatsapp/campaigns/new"
            element={
              <PrivateRoute>
                <Layout>
                  <WhatsAppCampaignBuilder />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/whatsapp/campaigns/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <WhatsAppCampaignDetail />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Email campaigns */}
          <Route path="/email/campaigns" element={<PrivateRoute><Layout><EmailCampaigns /></Layout></PrivateRoute>} />
          <Route path="/email/campaigns/new" element={<PrivateRoute><Layout><EmailCampaignBuilder /></Layout></PrivateRoute>} />
          <Route path="/email/campaigns/:id" element={<PrivateRoute><Layout><EmailCampaignDetail /></Layout></PrivateRoute>} />
          <Route path="/email/suppressions" element={<PrivateRoute><Layout><EmailSuppressions /></Layout></PrivateRoute>} />
          <Route path="/email/templates" element={<PrivateRoute><Layout><EmailTemplates /></Layout></PrivateRoute>} />
          <Route path="/email/settings" element={<PrivateRoute><Layout><EmailProviderSettings /></Layout></PrivateRoute>} />
          <Route
            path="/whatsapp/embedded-signup-config"
            element={
              <PrivateRoute>
                <Layout>
                  <EmbeddedSignupConfig />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/whatsapp/waba-registrations"
            element={
              <PrivateRoute>
                <Layout>
                  <WABARegistrations />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony"
            element={
              <PrivateRoute>
                <Layout>
                  <TelephonyDashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/agents"
            element={
              <PrivateRoute>
                <Layout>
                  <AIAgents />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route path="/agents/:id/config" element={<PrivateRoute><Layout><AgentConfigEditor /></Layout></PrivateRoute>} />
          <Route
            path="/agents/memory-profiles"
            element={
              <PrivateRoute>
                <Layout>
                  <MemoryProfiles />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Edit and Configure are merged: /edit now renders the unified editor so old links still work. */}
          <Route
            path="/agents/:id/edit"
            element={
              <PrivateRoute>
                <Layout>
                  <AgentConfigEditor />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/agents/create"
            element={
              <PrivateRoute>
                <Layout>
                  <AIAgentCreation />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/campaigns/create"
            element={<Navigate to="/telephony/campaigns?variant=ai" replace />}
          />
          <Route
            path="/telephony/ivr"
            element={
              <PrivateRoute>
                <Layout>
                  <IVRApps />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/softphone"
            element={
              <PrivateRoute>
                <Layout>
                  <Softphone />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/live-calls"
            element={
              <PrivateRoute>
                <Layout>
                  <LiveCalls />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/ivr/create"
            element={
              <PrivateRoute>
                <Layout>
                  <IVRCreation />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/ivr/switch-sync"
            element={
              <PrivateRoute>
                <Layout>
                  <IvrSwitchSync />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/telephony/ivr/app"
            element={
              <PrivateRoute>
                <Layout>
                  <IvrAppEditor />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Legacy path — canonical home is /user-directory/vip-management */}
          <Route path="/telephony/vip-management" element={<Navigate to="/user-directory/vip-management" replace />} />
          {/* Legacy path — canonical home is /user-directory/dnd-management */}
          <Route path="/telephony/dnd-management" element={<Navigate to="/user-directory/dnd-management" replace />} />
          <Route
            path="/telephony/business-hours"
            element={
              <PrivateRoute>
                <Layout>
                  <BusinessHours />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Workflows — same gate as Voice Campaigns (no route roles; the nav entry is MANAGERS). */}
          <Route path="/workflows" element={<PrivateRoute><Layout>{lazyPage(<WorkflowsList />)}</Layout></PrivateRoute>} />
          <Route path="/workflows/runs/:runId" element={<PrivateRoute><Layout>{lazyPage(<WorkflowRunDetail />)}</Layout></PrivateRoute>} />
          <Route path="/workflows/:id" element={<PrivateRoute><Layout>{lazyPage(<WorkflowEditor />)}</Layout></PrivateRoute>} />
          <Route path="/workflows/:id/runs" element={<PrivateRoute><Layout>{lazyPage(<WorkflowRuns />)}</Layout></PrivateRoute>} />
          <Route
            path="/telephony/campaigns"
            element={
              <PrivateRoute>
                <Layout>
                  <VoiceCampaigns />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* /telephony/obd-campaigns is gone for good. Bulk outbound dialling
              is no longer a separate surface: campaigns.call_type now says
              whether a call plays a recording, waits for a keypress, runs an
              IVR flow or holds a conversation, so there is nothing for this
              deep-link to land on. */}
          <Route
            path="/revenue"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="Revenue" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/user-directory"
            element={
              <PrivateRoute>
                <Layout>
                  <UserDirectoryOverview />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/user-directory/contacts"
            element={
              <PrivateRoute>
                <Layout>
                  <Contacts />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Declared BEFORE /:id — "duplicates" would otherwise be read as a
              contact id and render a profile that does not exist. */}
          <Route
            path="/settings/provider-access"
            element={
              <PrivateRoute>
                <Layout>
                  <ProviderAccess />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/sign-in-providers"
            element={
              <PrivateRoute>
                <Layout>
                  <SocialSignIn />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/contact-ceiling"
            element={
              <PrivateRoute>
                <Layout>
                  <ContactGate />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/data-privacy"
            element={
              <PrivateRoute>
                <Layout>
                  <DataPrivacy />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/bookings"
            element={
              <PrivateRoute>
                <Layout>
                  <BookingIntegrations />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/platform/portal-profiles"
            element={
              <PrivateRoute>
                <Layout>
                  <PortalProfiles />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/user-directory/contacts/duplicates"
            element={
              <PrivateRoute>
                <Layout>
                  <ContactDuplicates />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* One contact: identity plus their history per channel. Declared AFTER
              the list route so the literal path still wins over the :id pattern. */}
          <Route
            path="/user-directory/contacts/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <ContactProfile />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/user-directory/vip-management"
            element={
              <PrivateRoute>
                <Layout>
                  <VIPManagement />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/user-directory/dnd-management"
            element={
              <PrivateRoute>
                <Layout>
                  <DNDManagement />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <PrivateRoute>
                <Layout>
                  <AuditLogs />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Master-admin only. The page checks the role itself and the backend
              enforces hasRole('MASTER_ADMIN') — the sidebar merely hides the link,
              which is not access control. */}
          {/* Master-admin only: the per-provider feature allow-list
              (/v1/feature-grants, MASTER_ADMIN/PLATFORM_ADMIN on the server). */}
          <Route
            path="/platform/feature-access"
            element={
              <PrivateRoute>
                <Layout>
                  <FeatureAccess />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Green Giraffe only; the server enforces it and the page shows its 403. */}
          <Route
            path="/platform/seats"
            element={
              <PrivateRoute>
                <Layout>
                  <Seats />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/platform/service-controls"
            element={
              <PrivateRoute>
                <Layout>
                  <FeatureLocks />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Provider roles open this too since provider reassign: the page renders
              ProviderNumberOwnership for them (gate inside the page; server enforces
              the provider tree). The master view below stays master-only.
              Master-admin only, same reasoning: /v1/numbers acquire and release
              are hasRole('MASTER_ADMIN'), and moving a number decides who may
              present it as caller ID and who is billed for it. Deliberately not
              under /telephony — this edits provider_numbers (ownership), while
              /phones edits phone_numbers (inbound routing). They are different
              tables and confusing them is the mistake the page guards against. */}
          <Route
            path="/platform/numbers"
            element={
              <PrivateRoute>
                <Layout>
                  <NumberOwnership />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/sms"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="SMS" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/sms/setup"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="SMS Setup" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/sms/messages"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="SMS Messages" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/sms/campaigns"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="SMS Campaigns" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/rcs"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="RCS" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/rcs/setup"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="RCS Setup" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/rcs/rich-cards"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="RCS Rich Cards" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/rcs/campaigns"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="RCS Campaigns" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/rcs/messages"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="RCS Message Logs" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/billing"
            element={
              /* Platform only (2026-10-14): every call on this page is platform-wide server-side. */
              <PrivateRoute roles={PLATFORM_BILLING_ROLES}>
                <Layout>
                  <BillingDashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/billing/accounts"
            element={
              /* Platform only (2026-10-14): every call on this page is platform-wide server-side. */
              <PrivateRoute roles={PLATFORM_BILLING_ROLES}>
                <Layout>
                  <CreditAccounts />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/billing/rates"
            // The rate cards that billing actually charges by (/billing/rates). Until
            // 2026-10-13 this rendered RateCardsUsage, a per-prefix metering console whose
            // backend (/billing/rate-cards) does not exist here, so the page showed
            // "Not Found" and the rate terms built for go-live were unreachable.
            element={
              <PrivateRoute>
                <Layout>
                  <ServiceRates />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/billing/invoices"
            element={
              <PrivateRoute>
                <Layout>
                  <Invoices />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/billing/payments"
            element={
              <PrivateRoute>
                <Layout>
                  <Payments />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/billing/usage"
            element={
              <PrivateRoute>
                <Layout>
                  <UsageDashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Prepaid liability; the backend refuses everyone outside these roles. */}
          <Route
            path="/billing/deferred-revenue"
            element={
              <PrivateRoute roles={DEFERRED_REVENUE_ROLES}>
                <Layout>
                  <DeferredRevenue />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* The current organisation's own bill; providers, businesses and platform staff. */}
          <Route
            path="/my-billing"
            element={
              <PrivateRoute roles={MY_BILLING_ROLES}>
                <Layout>
                  <MyBilling />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Our team's test numbers; platform staff only, as the backend. */}
          <Route
            path="/billing/test-numbers"
            element={
              <PrivateRoute roles={TEST_NUMBER_ROLES}>
                <Layout>
                  <TestNumbers />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Gateway merchant accounts (SSM parameter names only): MASTER_ADMIN only, as the backend. */}
          <Route
            path="/billing/payment-gateways"
            element={
              <PrivateRoute roles={['master_admin']}>
                <Layout>
                  <PaymentGateways />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Holds credentials for the external voice-agent runtime — admins only. */}
          <Route
            path="/crm/integrations"
              element={
                <PrivateRoute roles={DEVELOPER_ROLES}>
                  <Layout>
                    <CrmIntegrations />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* What the pipeline did after each call — the only place a failed
                CRM sync is visible without a SQL query. */}
            <Route
              path="/crm/call-activity"
              element={
                <PrivateRoute roles={DEVELOPER_ROLES}>
                  <Layout>
                    <CallActivity />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* Every Custom API push, once per provider and call. Roles are the
                endpoint's own @PreAuthorize list — platform and provider admins. */}
            <Route
              path="/crm/custom-api-deliveries"
              element={
                <PrivateRoute roles={CUSTOM_API_DELIVERY_ROLES}>
                  <Layout>
                    <CustomApiDeliveries />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* What the agents captured — leads, bookings, messages. Same
                audience as Contacts, deliberately: a capture is about a contact
                and carries the same caller detail. The second path is one
                person's history; there is no per-call path, because no capture
                row carries a call id. */}
            {/* The shape of what the agents captured, as opposed to the rows
                themselves. Same audience as the list it summarises. */}
            <Route
              path="/crm/lead-overview"
              element={
                <PrivateRoute>
                  <Layout>
                    <LeadOverview />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/crm/captures"
              element={
                <PrivateRoute>
                  <Layout>
                    <Captures />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/crm/captures/contact/:contactId"
              element={
                <PrivateRoute>
                  <Layout>
                    <Captures />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* Services, opening hours and the diary for the built-in booking system. */}
            <Route
              path="/crm/booking-setup"
              element={
                <PrivateRoute roles={DEVELOPER_ROLES}>
                  <Layout>
                    <BookingSetup />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* Credentials to a customer's live booking system — admins only. */}
            <Route
              path="/agents/voice-ai-integrations"
            element={
              /* Green Giraffe's platform Voice AI config: master/platform admins only (14 Oct 2026). */
              <PrivateRoute roles={['master_admin', 'platform_admin']}>
                <Layout>
                  <VoiceAiIntegrations />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Holds database credentials for external switches — admins only. */}
          <Route
            path="/telephony/data-sources"
            element={
              <PrivateRoute roles={DEVELOPER_ROLES}>
                <Layout>
                  <CallDataSources />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/developer/api-keys"
            element={
              <PrivateRoute roles={DEVELOPER_ROLES}>
                <Layout>
                  <ApiKeys />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/developer/webhooks"
            element={
              <PrivateRoute roles={DEVELOPER_ROLES}>
                <Layout>
                  <Webhooks />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/developer/webhook-stubs"
            element={
              <PrivateRoute roles={STUB_ROLES}>
                <Layout>
                  <WebhookStubs />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/developer/docs"
            element={
              <PrivateRoute roles={DEVELOPER_ROLES}>
                <Layout>
                  <ApiDocumentation />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/developer/api-tester"
            element={
              <PrivateRoute roles={API_TESTER_ROLES}>
                <Layout>
                  <ApiTester />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Layout>
                  <Settings />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <Layout>
                  <Notifications />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="Analytics" />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/support"
            element={
              <PrivateRoute>
                <Layout>
                  <ComingSoon feature="Support" />
                </Layout>
              </PrivateRoute>
            }
          />
        </Routes>
        {/* Floating softphone, on every page. Renders nothing unless the user's
            role allows it, so it is inert for everyone else — including on the
            login screen, where there is no role yet. */}
        <SoftphoneWidget />
        </Router>
        </PortalProfileProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;